/**
 * The documents the rule enables
 */
export type DocumentContext = Record<string, unknown>
export interface RuleDocument {
  count: number
  docId: number
  mod?: number
  requestFields?: ComprehensiveRequestFields[]
  description?: string
  createdBy?: number
  context?: DocumentContext
}
export interface PoolDocument {
  poolId: number
  context: string
  filter?: string
}

export interface ComprehensiveRequestFields {
  fieldId: number
  name?: string
  type?: DynamicRequestFieldType
  sequence: number
  isMandatory?: boolean
  internalName?: string
  valueKey?: string
  options?: any
}

/**
 * The collection of rules for lender defined rules
 */
export interface RuleCollection {
  noRules?: true // If present and true no rules have to evaluate for this to be true.
  mustRules: number[] // All rules in this array must match true
  anyRules: number[][] // At least one rule in this array must match true.
  complexRule?: string // Evaluate "and", "or" & "not" functions over ruleIds
}

// Base lender defined rules
interface BaseLenderDefinedRule {
  id: number
  rules: RuleCollection // The list of the rules that need to be matched against.
  loanOptionId: number
  dynamicRules?: string
}

// The original lender defined rule document rules
export interface LenderDefinedDocIdRule extends BaseLenderDefinedRule {
  documents: RuleDocument[]
}

// The new lender defined pool document rules
export interface LenderDefinedPoolIdRule extends BaseLenderDefinedRule {
  documents: PoolDocument[]
}

// The lender defined rules from DB
export interface LenderDefinedRule extends BaseLenderDefinedRule {
  documents: PoolDocument[] | RuleDocument[]
}

export type DynamicDocOperation = '===' | '<' | '>' | '>=' | '<=' | '!==' | '*==' | '*!='

// Rules used to determine if a document is required or not.
export interface DynamicDocumentRule {
  id: number
  dataKey: string // The key used to store the user data.
  operation: DynamicDocOperation // The comparison operator
  value: string // The required value to match against.
}

export enum DynamicDocumentCategory {
  SUPPORTING = 'supporting',
  APPLICATION = 'application',
  POSTAPPROVAL = 'postApproval',
  LENDERREQUESTED = 'lenderRequested',
  OTHER = 'other',
}

export enum DynamicDocumentType {
  SYSTEM = 1,
  CUSTOM = 2,
}

export enum DynamicDocumentActionType {
  UPLOAD_DOCUMENT = 1,
  TRIGGER_BANKSTATEMENTS = 2,
  DYNAMIC_FIELDS = 3,
  TRIGGER_BANKSTATEMENTS_SIX_MONTHS = 4,
  SELLER_DEALER = 5,
  BANK_STATEMENTS_SPECIFIC_ACCOUNT = 6,
  BANK_STATEMENTS_SPECIFIC_ACCOUNT_SIX_MONTHS = 7,
}

export enum DynamicDocumentParseType {
  UNKNOWN = 0,
  LIABILITY_SECURED = 1,
  LIABILITY_PL = 2,
  LIABILITY_CC = 3,
  LIABILITY_MORTGAGE = 4,
  LIABILITY_BNPL = 5,
  LIABILITY_OTHER = 6,
  AVERAGE_MONTHLY_EXPENSES = 7,
  MISSING_LIABILITY = 8,
  PRIMARY_INCOME = 9,
  LIABILITY_MISMATCH_BALANCE = 10,
  LIABILITY_MISMATCH_LIMIT = 11,
  LIABILITY_MISMATCH_REPAYMENTS = 12,
  LIABILITY_MISMATCH_JOINT_ACCOUNT = 13,
  EMPLOYMENT_STATUS = 14,
  PAYSLIPS_PRIMARY_INCOME = 15,
}

export interface DynamicDocsData {
  data: string[]
}

// The document to be uploaded.
export interface DynamicDocument {
  id: number
  name: string // Name of the document.
  tooltip: string // The tooltip text.
  dynamicTooltip?: string
  label: string // Used to store the document
  category: DynamicDocumentCategory // What page the document is displayed on.
  type: DynamicDocumentType // Is it a custom document
  actionType?: DynamicDocumentActionType // What action to perform in the UI, default is UPLOAD_DOCUMENT
  parseType?: DynamicDocumentParseType // Try and parse the blob
  createdBy?: number // The user who created the document. can be dynamic_doc_rules: 1000
  poolId?: number // The pool id the document is associated with.
  dynamicData?: DynamicDocsData // Dynamic Data of document to be used for parsing labels (dynamic request field)
}
export interface DynamicDocItem extends DynamicDocument {
  count: number
  description?: string
  requestFields?: ComprehensiveRequestFields[]
  context?: DocumentContext
}

export interface DynamicDocument_Create extends Omit<DynamicDocument, 'id'> {
  createdBy: number
}

export enum DynamicRequestFieldType {
  TEXT = 1,
  NUMBER = 2,
  SELECT = 3,
  TEXT_AREA = 4,
  LABEL = 5,
  CHECKBOX = 6,
}

export interface LoanApplicationDynamicRequest {
  dynamicRequestId: number
  loanId: number
  dynamicDocId: number
  data: Record<string, any>
  createdAt: Date
  deletedAt?: Date
}
